import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { ParticipationStatus } from '../../models/Event'
import { eventParticipationStatusSchema as participationStatuseSchema } from '../../models/schema'
import {
  createParticipationStatus as createParticipationStatusAPI,
  deleteParticipationStatus as deleteParticipationStatusAPI,
  fetchParticipationStatusById,
  fetchParticipationStatuses,
  updateParticipationStatus as updateParticipationStatusAPI,
} from '../../services/membership/ParticipationStatusService'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<ParticipationStatus> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'participationStatus',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getParticipationStatusesSuccess,
  setQuery: setParticipationStatusQuery,
  reset: resetParticipationStatusestate,
  resetQueryAndIds: resetParticipationStatusQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: ParticipationStatus }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectParticipationStatuses = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedParticipationStatus = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectParticipationStatusById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectParticipationStatusesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedParticipationStatus =
  (participationStatus: ParticipationStatus | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<ParticipationStatus>(participationStatus)
      )
    )

export const getParticipationStatuses = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchParticipationStatuses(params),
    [participationStatuseSchema],
    slice.actions
  )

export const createParticipationStatus = (
  participationStatus: ParticipationStatus
) =>
  SBAPI.CreateDispatched(
    createParticipationStatusAPI(participationStatus),
    participationStatuseSchema,
    slice.actions
  )

export const getParticipationStatusWithId = (id: number) =>
  SBAPI.FetchDispatched<ParticipationStatus>(
    fetchParticipationStatusById(id),
    participationStatuseSchema,
    slice.actions
  )

export const updateParticipationStatus = (
  participationStatus: ParticipationStatus
) =>
  SBAPI.UpdateDispatched<ParticipationStatus>(
    updateParticipationStatusAPI(participationStatus),
    participationStatus,
    slice.actions
  )

export const deleteParticipationStatus = (
  participationStatus: ParticipationStatus
) =>
  SBAPI.DestroyDispatched<ParticipationStatus>(
    deleteParticipationStatusAPI(participationStatus),
    participationStatus,
    slice.actions
  )
